import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { BuildingConfigWizard } from '../../components/BuildingConfigWizard'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useAsset } from '../../hooks/useQueries'

type BuildingSearch = {
  building_group_uid?: string
  asset_uid?: string
  step?: number
}

export const Route = createFileRoute('/$org/createBuilding')({
  component: RouteComponent,
  validateSearch: (search: any): BuildingSearch => {
    const stepValue = search.step
    const asset_uid = search.asset_uid as string | undefined
    let step: number | undefined = undefined

    // This wizard owns steps 3-5 (documents, properties questionnaire, fiabilisation).
    if (typeof stepValue === 'number') {
      step = stepValue >= 3 && stepValue <= 5 ? stepValue : undefined
    } else if (typeof stepValue === 'string') {
      const parsed = parseInt(stepValue, 10)
      if (!isNaN(parsed) && parsed >= 3 && parsed <= 5) {
        step = parsed
      }
    }

    // Default to the first config step (documents).
    if (step === undefined) {
      step = 3
    }

    return {
      building_group_uid: search.building_group_uid as string | undefined,
      asset_uid,
      step,
    }
  },
})

function RouteComponent() {
  const { org } = Route.useParams()
  const { building_group_uid, asset_uid, step } = Route.useSearch()
  const { t } = useTranslation('assets')
  const { t: tCommon } = useTranslation('common')

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: tCommon('nav.home'), path: `/${org}` },
    { title: t('list.title_all'), path: `/${org}/assets` },
    { title: t('create_building.breadcrumb_config') }
  ])

  // Fetch asset data if asset_uid is provided to resolve the parent building group.
  const { data: assetData } = useAsset(org, asset_uid || '')

  // Documents and the questionnaire are group-level. When resuming via asset_uid
  // (a building), use its parent as the building group.
  let effectiveBuildingGroupUid = building_group_uid

  if (asset_uid && assetData) {
    effectiveBuildingGroupUid = assetData.parent_asset_uid || assetData.uid
  } else if (asset_uid && !assetData) {
    // asset_uid provided but data isn't loaded yet — don't render yet
    return <div>{tCommon('status.loading')}</div>
  }

  return (
    <BuildingConfigWizard
      buildingGroupUid={effectiveBuildingGroupUid}
      resumeStep={step}
    />
  )
}
